import type { InferTranslation } from "typed-locale";

export const en = {
	locale: "en",
	seo: {
		homeTitle: "Freelance software developer, web and mobile",
		homeDescription:
			"Simon Boisset, freelance software developer based in Nantes: web and mobile apps in TypeScript, React, React Native and Node.js. Team reinforcement, app takeovers, Expo migrations. Remote with teams in France and abroad.",
		expoMigrationTitle: "React Native to Expo migration",
		expoMigrationDescription:
			"Move a legacy React Native app to Expo without blocking releases: warning signs, risks covered, a four-step method, client feedback and FAQ.",
		questoveryTitle: "Questovery: geolocated treasure hunts",
		questoveryDescription:
			"Questovery, the product I design, build and sell on my own: treasure hunts, scavenger hunts and guided tours, with a web workspace and an iOS and Android app.",
		aboutTitle: "Background: from lasers to code",
		aboutDescription:
			"Trained physicist, five years in the laser industry, then software development since 2020: startups, freelance missions and my own products.",
		blogTitle: "Blog",
		cvTitle: "Resume",
		cvDescription:
			"Resume of Simon Boisset, freelance software developer, web and mobile: missions, products, skills and education. PDF version available.",
	},
	nav: {
		brand: "Simon Boisset",
		home: "Home",
		expoMigration: "Expo migration",
		questovery: "Questovery",
		about: "Background",
		blog: "Blog",
		cv: "Resume",
		bookCall: "Book a call",
		menu: "Menu",
		skip: "Skip to content",
	},
	footer: {
		description:
			"Freelance software developer, web and mobile. TypeScript apps, from the first build to the next release.",
		site: "Site",
		contact: "Contact",
		legal: "Legal",
		scheduleCall: "Book a call",
		blog: "Blog",
		docs: "Legal documents",
		legalNotice: "Legal notice",
		privacy: "Privacy",
		withdrawConsent: "Withdraw analytics consent",
		manageConsent: "Manage analytics",
		github: "GitHub",
		linkedin: "LinkedIn",
		cvPdf: "Resume (PDF)",
	},
	common: {
		bookCall: "Book a call",
		sendEmail: "Send an email",
		downloadCv: "Download the resume (PDF)",
		example: "Example",
		visit: "Visit",
	},
	consent: {
		title: "Audience measurement",
		description:
			"With your consent, PostHog measures page visits, traffic sources and clicks towards Cal.com or email. No session recordings or automatic interaction capture.",
		retention:
			"Your choice is remembered for six months. You can change it at any time through ‘Manage analytics’ at the bottom of the page.",
		policyLink: "View the privacy policy",
		accept: "Accept",
		deny: "Decline",
		close: "Close",
	},
	notFound: {
		label: "Error 404",
		title: "This page drifted away.",
		description: "The page you're looking for doesn't exist or has been moved.",
		backHome: "Back to Home",
	},
	blog: {
		heroLabel: "Blog",
		heroTitle: "Field notes on React Native and Expo.",
		description:
			"Practical notes on migrations, release workflows, mobile architecture, and the product decisions behind Questovery.",
		featured: "Featured",
		latest: "Latest articles",
		empty: "No blog posts published yet.",
		back: "Back to blog",
		other: "Other articles",
		bookCtaButton: "Book a call",
		errorTitleList: "Error loading blog",
		errorTitleDetail: "Error loading post",
		errorFallback: "An unexpected error occurred",
	},
	docs: {
		heroLabel: "Documents",
		heroTitle: "Legal documentation",
		heroDescription: "Find the required documents and legal information.",
		effectiveDate: "Effective date",
		empty: "No documents available.",
		cardDescriptions: {
			legal: "Publisher, hosting, and contact details.",
			privacy: "How data is collected and your rights.",
		},
	},
	scenes: {
		prism: {
			era: "2014 – 2020",
			title: "Laser physics",
			subtitle: "Master Lille 1 · Amplitude Laser",
			pivot: "2020",
			rays: {
				product: { label: "Product", value: "UX/UI, marketing, sales" },
				ai: { label: "Agentic AI", value: "Claude Code · Codex · MCP" },
				backend: { label: "Back end", value: "Node.js · oRPC · PostgreSQL" },
				web: { label: "Web", value: "React · TanStack Start" },
				mobile: { label: "Mobile", value: "React Native · Expo" },
			},
		},
		migration: {
			before: "Before · legacy RN",
			after: "After · Expo",
			arrow: "migration",
			pipeline: "Release pipeline",
			build: "build",
			release: "release",
			stores: "stores",
		},
		questovery: {
			step: "Step 3 / 5",
			points: "240 pts",
			title: "The old wash house",
			scan: "Scan the step QR code",
			validate: "Validate step",
			team: "Team 4",
		},
		chaban: {
			status: "Bridge raised",
			app: "Mon Pont Chaban",
			time: "9:12 pm",
			title: "Closing at 9:30 pm",
			body: "Bridge raised until 11:10 pm, plan a detour",
		},
	},
	home: {
		hero: {
			title: "Freelance software developer, web and mobile",
			titleLead: "Freelance software developer,",
			titleMark: "web & mobile",
			intro:
				"I design and build web and mobile apps in TypeScript, from the interface to the API, all the way to production. Since 2020, for startups and SMEs, as extra hands in the team or as tech lead, with React, React Native and Node.js.",
			ctaPrimary: "Book a call",
			ctaSecondary: "See my background",
			proofs: {
				since: "Shipping since 2020",
				platforms: "Web · iOS · Android",
				stack: "TypeScript end to end",
			},
		},
		contacts: {
			email: "email",
			site: "site",
			linkedin: "linkedin",
			github: "github",
			location: "location",
			locationValue: "Nantes, remote",
		},
		services: {
			title: "What I do",
			subtitle: "on a mission",
			team: {
				title: "Team reinforcement",
				description:
					"Shipping the next features with your team: web, mobile and API, all the way to production.",
			},
			takeover: {
				title: "Taking over an app",
				description:
					"Understanding an existing codebase quickly, clearing technical priorities, putting structure back where it is needed.",
			},
			creation: {
				title: "Building an app",
				description:
					"Designing and shipping a web or mobile app from scratch, aligned with your existing product.",
			},
			lead: {
				title: "Tech lead",
				description:
					"Evolving the architecture and supporting the team in its technical choices.",
			},
		},
		profile: {
			title: "Profile",
			specialty: {
				label: "Specialty",
				value:
					"Web and mobile apps in TypeScript: React, React Native, Node.js, from design to production.",
			},
			production: {
				label: "Production",
				value:
					"Build pipelines, store releases, maintenance of apps in production.",
			},
			product: {
				label: "Product",
				value:
					"Two products designed, launched and run on my own, from the interface to sales.",
			},
			training: {
				label: "Education",
				value:
					"Master's degree in laser physics, then five years in the laser industry.",
			},
		},
		journey: {
			title: "My path",
			subtitle: "at a glance",
			lanes: {
				laser: "Laser",
				code: "Code",
				products: "Products",
				around: "Around",
			},
			bars: {
				master: "Master",
				amplitude: "Amplitude Laser · laser industry",
				selfTaught: "self-taught, in parallel",
				silbo: "Silbo · full-stack employee",
				linote: "LiNote",
				vaerdict: "VAERDICT",
				ccp: "CAMPING-CAR PARK",
				mobiapps: "MOBIAPPS",
				chaban: "Mon Pont Chaban",
				questovery: "Questovery",
				around: "AI · marketing · sales",
			},
			today: "today",
		},
		missions: {
			title: "What I delivered",
			subtitle: "context · action · result",
			labels: {
				context: "context",
				action: "action",
				result: "result",
			},
			ccp: {
				company: "CAMPING-CAR PARK",
				period: "03.2024 – 09.2025",
				role: "React Native · mobile lead",
				stack: "Expo · CI/CD · maps",
				context:
					"Booking app in production, built on an ageing React Native codebase.",
				action:
					"Migration to Expo: unmaintained libraries replaced, build and release pipeline configured. Then a redesign in 2025, booking and maps, with the product and back-end teams.",
				result:
					"An up-to-date Expo codebase, more reliable releases and a team supported over time.",
			},
			mobiapps: {
				company: "MOBIAPPS",
				period: "10.2025",
				role: "React Native · Expo migration",
				stack: "Expo · config plugins",
				context: "Legacy React Native app to modernise.",
				action:
					"Migration to Expo: native configuration, Expo plugins, dependencies and React Navigation.",
			},
			vaerdict: {
				company: "VAERDICT",
				period: "since 2024",
				role: "Full-stack · tech lead",
				stack: "React · Node.js · TypeScript",
				context: "Business web app to evolve, with an internal team.",
				action: "Re-architecture to make it scale, tech lead of the team.",
				result: "A more scalable codebase, a supported team.",
			},
			linote: {
				company: "LiNote",
				period: "2023",
				role: "React Native · new app",
				stack: "Expo · iOS · Android",
				context: "Web communication platform with no mobile app.",
				action:
					"App built from scratch: messaging, photo sharing, video calls, notifications and alerts.",
				result: "Key features shipped on iOS and Android.",
			},
			silbo: {
				company: "Silbo",
				period: "2020 – 2022 · employee",
				role: "Full-stack",
				stack: "Node.js · TypeScript · React · React Native",
				context: "Web and field apps for healthcare.",
				action: "Full-stack development, real time, production operations.",
				result: "Apps shipped and used every day.",
			},
		},
		testimonialsSection: {
			title: "Client feedback",
			subtitle: "from missions",
		},
		testimonials: {
			eric: {
				name: "Eric",
				quote:
					"We started from a fairly complex legacy codebase. Simon took over the project, brought structure back to the whole stack, and still supports us day to day.",
			},
			julie: {
				name: "Julie",
				quote:
					"Simon built our React Native app from scratch while keeping it aligned with the existing web app. In a few weeks, we had reminders, messaging, photo sharing, and video calls.",
			},
			matthieu: {
				name: "Matthieu",
				quote:
					"We had been dragging this Expo migration for more than a year. Simon reassured us, delivered it in two weeks, and we were able to publish a clean version afterwards.",
			},
			thomas: {
				name: "Thomas",
				quote:
					"Simon joined a project where frontend, backend, mobile, and infrastructure were all tied together. He quickly understood how everything fit together, which saved us a lot of back and forth.",
			},
			romuald: {
				name: "Romuald",
				quote:
					"Simon migrated three of our React Native apps to Expo after more than 9 years of maintenance. The migration went smoothly, and we are already seeing the benefits: much faster upgrades and finally reliable builds.",
			},
			antoine: {
				name: "Antoine",
				quote:
					"Our mobile codebase had become too complex: too many special cases, too many dependencies, and not much certainty left. Simon helped us regain control and put a clear structure back in place.",
			},
		},
		focus: {
			title: "One expertise, one product",
			subtitle: "each with its own page",
			migration: {
				meta: "expertise",
				name: "React Native → Expo migration",
				lede: "Moving a legacy React Native app to Expo without blocking releases, including apps maintained for more than nine years.",
				link: "See the Expo migration",
			},
			questovery: {
				meta: "my product · since 2024",
				name: "Questovery",
				lede: "Treasure hunts, scavenger hunts and guided tours: a web workspace for organisers, an iOS and Android app for players. Designed, built and sold on my own.",
				link: "Discover Questovery",
			},
		},
		products: {
			title: "Products",
			subtitle: "designed, built and run on my own",
			questovery: {
				meta: "since 2024 · founder",
				name: "Questovery",
				lede: "Geolocated treasure hunts: a web workspace for organisers, an iOS and Android app for teams.",
				points: {
					offline: "Offline mode and sync, GPS, QR codes, media.",
					ai: "Agentic AI to speed up development.",
					sales: "Marketing and sales of the product.",
				},
				stack: "TanStack Start · oRPC · React · React Native · Expo",
				link: "questovery.com",
			},
			chaban: {
				meta: "since 2022",
				name: "Mon Pont Chaban",
				lede: "Lift times of the Chaban-Delmas bridge in Bordeaux: live status and personalised alerts.",
				points: {
					alerts: "Push and email alerts set per day and hour, iCal calendar.",
					effect: "Effect TS back end, Bordeaux Métropole data.",
					stack: "Web, iOS and Android on one TypeScript base.",
				},
				stack: "same stack as Questovery · Effect TS",
				link: "pont-chaban-delmas.com",
			},
		},
		skills: {
			title: "Skills",
			mobile: {
				title: "Mobile",
				items:
					"React Native, Expo, EAS, push notifications, geolocation, store releases",
			},
			web: { title: "Web", items: "TypeScript, React, TanStack Start" },
			backend: {
				title: "Back end & delivery",
				items:
					"Node.js, oRPC, Effect TS, Bun, PostgreSQL, Drizzle, Docker, GitHub Actions, CI/CD",
			},
			ai: { title: "Agentic AI", items: "Claude Code, Codex, MCP" },
			product: {
				title: "Product",
				items: "UX/UI, user journeys, marketing, sales",
			},
			educationLabel: "Education",
			master: "Master's in Laser Physics",
			masterDetail: "Université Lille 1, 2014",
			selfTaught: "Software development",
			selfTaughtDetail: "self-taught, project by project, since 2015",
		},
		contact: {
			title: "Contact",
			body: "I enjoy joining an existing product, understanding its constraints and moving it forward with the team in place. If your web or mobile app needs an extra pair of hands, I would be glad to talk.",
		},
	},
	expoMigration: {
		hero: {
			kicker: "Expo migration",
			title: "Move a React Native app to Expo without blocking releases",
			intro:
				"I move legacy React Native apps to Expo while the product keeps shipping. Your team ends up with a build and release pipeline it controls.",
		},
		signs: {
			title: "When to migrate",
			subtitle: "the signs that keep coming back",
			upgrades: {
				title: "Upgrades are scary",
				description:
					"Every React Native or store SDK update turns into a project of its own.",
			},
			builds: {
				title: "Builds break",
				description:
					"iOS and Android builds fail for unclear reasons, and few people know how to fix them.",
			},
			libraries: {
				title: "Abandoned native libraries",
				description:
					"Dependencies are no longer maintained and block every upgrade.",
			},
			stalled: {
				title: "The migration keeps slipping",
				description:
					"Moving to Expo has been planned for months, and the right moment never comes.",
			},
		},
		risks: {
			title: "What I secure during a migration",
			subtitle: "where migrations get stuck",
			native: {
				title: "Native surface",
				description:
					"Dependencies, permissions, config plugins, prebuild, and modules that can block Expo.",
				bullet:
					"You get a compatibility map and clear replacement or fallback options.",
			},
			delivery: {
				title: "EAS and QA deployments",
				description:
					"EAS Build, Submit, Update, channels, CI, and QA distribution.",
				bullet:
					"You end up with automated EAS builds, test channels and OTA updates your team can trigger on its own.",
			},
			tailored: {
				title: "Tailored choices",
				description:
					"Trade-offs matched to your stack, store constraints, and product cadence.",
				bullet:
					"We pick the useful Expo options together instead of a generic workflow.",
			},
			handoff: {
				title: "Team ownership",
				description:
					"Decisions, playbooks, and trade-offs are written for the internal team.",
				bullet:
					"The migration ends with a workflow your team can run without me.",
			},
		},
		method: {
			title: "How a migration runs",
			subtitle: "four steps",
			steps: {
				map: {
					label: "map",
					title: "Understand the existing app",
					description:
						"Code, native dependencies, CI and critical flows reviewed before changing anything.",
					outcome: "Priorities and treatment order",
				},
				migrate: {
					label: "migrate",
					title: "Modernise without blocking",
					description:
						"Expo migration when it serves the need, incompatible libraries replaced, builds secured.",
					outcome: "An app that builds cleanly",
				},
				prove: {
					label: "release",
					title: "Ship for real",
					description:
						"Regular test builds, flows checked on iOS and Android, store release.",
					outcome: "A proven release path",
				},
				transfer: {
					label: "hand over",
					title: "Leave the team in control",
					description:
						"Commands, decisions and fallback options documented for maintenance.",
					outcome: "A team ready for the next releases",
				},
			},
		},
		testimonialsTitle: "They talk about it",
		faq: {
			title: "Frequent questions",
			duration: {
				question: "How long does an Expo migration take?",
				answer:
					"It depends on the native dependencies, the CI and the release setup. A focused migration can take two weeks; several older apps take longer. The initial review estimates the scope before any change.",
			},
			releases: {
				question: "Do releases have to stop during the migration?",
				answer:
					"No. The goal is to migrate while the product keeps moving, then prove the new release path on a version the team can inspect.",
			},
			after: {
				question: "Can you stay after the migration?",
				answer:
					"Yes. I can keep supporting the team on new features, releases and technical follow-up.",
			},
			remote: {
				question: "Do you work remotely?",
				answer: "Yes, remotely from Nantes, with teams in France and abroad.",
			},
		},
	},
	questovery: {
		hero: {
			kicker: "Questovery",
			title: "Questovery, my treasure-hunt product",
			intro:
				"Questovery lets you design, launch and run treasure hunts, scavenger hunts and guided tours. I have been designing, building, running and selling it on my own since 2024.",
		},
		roleLabel: "My role",
		stackLabel: "Stack",
		featuresLabel: "What it does",
		product: {
			title: "The product",
			meta: "since 2024 · founder",
			lede: "Create and run treasure hunts. Organisers prepare their routes and follow teams from a web workspace, players play on an iOS and Android app.",
			role: "Product design, UX/UI, full-stack and mobile development, deployment, maintenance, marketing and sales.",
			features: {
				app: "Mobile app: game flow, geolocation, QR codes, camera and media, with phone permissions handled.",
				web: "Web workspace: route creation, step setup, team management, live progress and results.",
				offline:
					"Field constraints: game actions stored locally then synced, no double counting, recovery after a network drop.",
				ops: "Mobile builds, web and server deployments, error tracking, tests on game rules, access rights and sync.",
			},
			stack:
				"TanStack Start · oRPC · React · React Native · Expo · PostgreSQL · Drizzle",
			link: "questovery.com",
		},
		uses: {
			title: "Who it is for",
			subtitle: "a few uses",
			teamBuilding: {
				title: "Team building",
				description: "Team challenges for company offsites and events.",
			},
			tours: {
				title: "Guided tours",
				description:
					"Discovery routes through a town or a venue, followed on the phone.",
			},
			parties: {
				title: "Birthdays and parties",
				description: "Ready-to-play treasure hunts for a birthday or a party.",
			},
		},
		lessons: {
			title: "What it brings to my missions",
			subtitle: "on the other side of the counter",
			production: {
				title: "Production, for real",
				description:
					"I publish and maintain my own apps: stores, updates, error tracking. I know what a failed release costs.",
			},
			product: {
				title: "A product mindset",
				description:
					"I make interface, pricing, marketing and sales decisions, not only technical ones.",
			},
			ai: {
				title: "Agentic AI every day",
				description:
					"Claude Code, Codex and MCP on a codebase that runs in production, with the review that requires.",
			},
		},
	},
	about: {
		hero: {
			kicker: "Background",
			title: "From lasers to code",
			intro:
				"Trained physicist, I spent five years in the laser industry before making software development my job.",
		},
		story: {
			title: "The path",
			laser: {
				period: "2014 – 2020",
				title: "Physics and lasers",
				body: "Master's degree in laser physics at Université Lille 1, then five years at Amplitude Laser. Optics, photonics, mathematical modelling: a scientific rigour I still apply to code.",
			},
			code: {
				period: "2015 – 2020",
				title: "Self-taught code",
				body: "Alongside, I learn development project by project: web and product first, then mobile architecture, DevOps and full-stack.",
			},
			startups: {
				period: "2020 – 2022",
				title: "First job in a startup",
				body: "Full-stack developer at Silbo, web and field apps for healthcare, real time and production operations.",
			},
			freelance: {
				period: "since 2023",
				title: "Freelance",
				body: "React Native and full-stack missions for startups and SMEs: LiNote, VAERDICT, CAMPING-CAR PARK, MOBIAPPS.",
			},
			products: {
				period: "since 2022",
				title: "My own products",
				body: "Mon Pont Chaban, then Questovery. Building them taught me product, marketing and sales, and how to use agentic AI on code that runs in production.",
			},
		},
		ai: {
			title: "AI in my work",
			body: "Daily use on products in production.",
			tools: "Claude Code · Codex · MCP",
		},
	},
	cv: {
		pageLabel: "Resume",
		download: "Download the PDF",
		print: "Print",
		header: {
			name: "Simon Boisset",
			intro:
				"Trained physicist, I spent five years in the laser industry before making software development my job. Since 2020 I have worked professionally, first inside startups, then as a freelancer for startups and SMEs, on web and mobile apps. Alongside, I build my own products.",
			location: "Nantes, remote",
		},
	},
} as const;

export type Translations = InferTranslation<typeof en>;
